import { Collapse } from "antd";
import type { FC } from "react";
import { useTranslation } from "react-i18next";

import type {
    AppConfigsFormConfiguration,
    AppConfigsFormJson,
    SubEntity,
} from "src/app/features/ApplicationConfigurations/types/AppConfigsTypes";
import type { AntCustomFormField } from "../../../../../components/Form/types/types";
import { deepClone } from "../../../../../core/utils/utilFunctions";
import { ApplicationConfigurationsForm } from "./ApplicationConfigurationsForm";

export interface ApplicationConfigurationsProps extends AntCustomFormField<AppConfigsFormJson> {}

export const ApplicationConfigurations: FC<ApplicationConfigurationsProps> = ({ value, onChange }) => {
    const { Panel } = Collapse;
    const { t } = useTranslation();

    const onChangeCallbackForms = (formData: AppConfigsFormConfiguration) => {
        if (!value) return;
        const { header } = formData;
        let subEntityArray: SubEntity[] = [];
        // clone created in order to mutate a readOnly object
        const newAppConfigsData = deepClone(value);

        // find index of form in wholeJson parent
        const findIndexOfFormInJson = (formName: string) => {
            const index = newAppConfigsData.findIndex((form: AppConfigsFormConfiguration) => form.header === formName);
            return index;
        };

        const copyChildDataToParent = (subEntity: SubEntity, subEntityIndex: number) => {
            const newChildData = () => {
                return [
                    {
                        key: `tab-${subEntity.reference}`,
                        configFields: subEntity.subEntityFields,
                    },
                ];
            };
            newAppConfigsData[subEntityIndex].configTabs = newChildData();
        };

        const index = findIndexOfFormInJson(header);
        if (index > -1) {
            newAppConfigsData[index] = formData;
        }

        // Check if subEntity/ies exists in onChange data. If so, move onChange object to subEntityTab
        if (formData.configTabs) {
            formData.configTabs.forEach((tab) => {
                if (tab.subEntity) {
                    subEntityArray = tab.subEntity;
                }
            });
        }

        // for each subEntity found in onChange form - find reference string, find parent form, copy child to parent
        subEntityArray.forEach((subEntity) => {
            const subEntityReference = subEntity?.reference;

            // subEntity form index in parent
            if (subEntityReference) {
                const subEntityIndex = findIndexOfFormInJson(subEntityReference);
                if (subEntityIndex > -1) {
                    // insert subEntity data into whole form object
                    copyChildDataToParent(subEntity, subEntityIndex);
                }
            }
        });

        if (onChange) {
            onChange(newAppConfigsData);
        }
    };

    const renderPanels = (data: AppConfigsFormJson) => {
        const dataArray = Array.isArray(data) ? data : []; // Ensure data is always an array
        return dataArray.map((dataItem) => {
            if (dataItem.isSubEntity) return null;

            const showArrow = !!dataItem.configTabs?.length;
            return (
                <Collapse key={`${dataItem.key}-renderCollapse`}>
                    <Panel header={t(dataItem.header)} key={`${dataItem.key}-renderPanel`} showArrow={showArrow}>
                        <ApplicationConfigurationsForm appConfigsFormData={dataItem} onChangeCallback={onChangeCallbackForms} />
                    </Panel>
                </Collapse>
            );
        });
    };

    return <Collapse>{renderPanels(value || [])}</Collapse>;
};
